import { cwd } from 'node:process';
import React from 'react';
import type { ReactElement } from 'react';
import { AuthProvider, LoginModeProvider } from '@wix/cli-auth';
import type { UserInfo } from '@wix/cli-auth';
import { render } from '@wix/cli-ui-kit';
import { DebugLogProvider, writeCrashReport } from '@wix/cli-debug-log';
import { getDebugLogFilePath } from '@wix/cli-core-definitions';
import { createPetriOverridesCookie } from '@wix/cli-astro-definitions';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import { BiProvider } from '@wix/cli-telemetry-react';
import { addUserFields, reportCommandStartEvent } from '@wix/cli-telemetry';
import type { Flow } from '@wix/cli-telemetry';
import type { AstroCommandServices } from '@wix/cli-command-contract';
import {
  CliError,
  CliErrorCode,
  cliErrorToError,
  stringifyError,
  isCliSystemError,
} from '@wix/cli-error';
import {
  HttpClientProvider,
  buildHttpClientHeaders,
} from '@wix/cli-http-client-react';
import type { Command, CommandUnknownOpts } from '@commander-js/extra-typings';
import { isType } from 'variant';
import { createCommandTransaction, PanoramaProvider } from '@wix/cli-panorama';
import type { ProjectModel } from '../model.js';
import { loadProjectModel, ProjectModelProvider } from '../model.js';
import { createAstroLogger } from '../components/DevCommand/utils/createAstroLogger.js';

function getFlow(model: ProjectModel): Flow {
  return model.config.projectType === 'App' ? 'Astro_App' : 'Headless';
}

async function initServices({
  model,
  command,
  flow,
  cliVersion,
  services,
}: {
  model: ProjectModel;
  command: CommandUnknownOpts;
  flow: Flow;
  cliVersion: string;
  services: AstroCommandServices;
}) {
  const { biLogger, errorReporter, panorama } = services;

  errorReporter.setTag('project', flow);
  errorReporter.setTag('project.app_id', model.config.appId);

  panorama.setDefaultClientData({
    cliProject: flow,
    cliAppId: model.config.appId,
  });

  if (model.config.projectType === 'App' && model.config.projectId != null) {
    errorReporter.setTag('project.id', model.config.projectId);
    panorama.setDefaultClientData({ cliProjectId: model.config.projectId });
  }

  if (model.config.projectType === 'Site') {
    errorReporter.setTag('project.site_id', model.config.siteId);
    panorama.setDefaultClientData({ cliSiteId: model.config.siteId });
  }

  biLogger.updateDefaults({
    flow,
    cliVersion,
    command: command.name(),
    _appId: model.config.appId,
  });
}

export async function renderCommand(
  command: Command,
  services: AstroCommandServices,
  cliVersion: string,
  tree: ReactElement
) {
  await runCommand(command, services, cliVersion, async (model) => {
    const { biLogger, errorReporter, panorama } = services;

    const debugLogFilePath = getDebugLogFilePath(model.projectFolder);

    const handleLogin = (userInfo: UserInfo) => {
      errorReporter.setUser({ id: userInfo.userId });
      addUserFields(biLogger, userInfo.userId);
    };

    const headers = buildHttpClientHeaders({
      flow: getFlow(model),
      command: command.name(),
      cliVersion,
    });

    await render(
      <BiProvider value={biLogger}>
        <ErrorReporterProvider value={errorReporter}>
          <PanoramaProvider value={panorama}>
            <LoginModeProvider value={services.loginMode}>
              <AuthProvider onLogin={handleLogin}>
                <HttpClientProvider
                  headers={headers}
                  cookies={createPetriOverridesCookie(
                    model.config.httpExperiments
                  )}
                >
                  <DebugLogProvider filePath={debugLogFilePath}>
                    <ProjectModelProvider value={model}>
                      {tree}
                    </ProjectModelProvider>
                  </DebugLogProvider>
                </HttpClientProvider>
              </AuthProvider>
            </LoginModeProvider>
          </PanoramaProvider>
        </ErrorReporterProvider>
      </BiProvider>
    );
  });
}

export async function runCommand(
  command: CommandUnknownOpts,
  services: AstroCommandServices,
  cliVersion: string,
  commandFn: (model: ProjectModel) => Promise<void>
) {
  const model = await loadProjectModel(cwd(), services.definedWixConfig);
  const flow = getFlow(model);

  await initServices({ model, command, flow, services, cliVersion });

  const debugLogFilePath = getDebugLogFilePath(model.projectFolder);
  const pluginLogger = createAstroLogger({
    isPrefixed: true,
  });

  const { pluginsInterface } = model;

  if (pluginsInterface) {
    pluginsInterface.onPluginError = (error, context) => {
      const pluginError = new CliError({
        code: CliErrorCode.PluginHandlerFailed({
          handlerName: context.handlerName,
          pluginName: context.pluginName,
        }),
        cause: error,
        info: {
          handlerName: context.handlerName,
          pluginName: context.pluginName,
        },
      });

      writeCrashReport(debugLogFilePath, pluginError, new Date());

      // Preventing recursive emitting of an error event.
      if (context.handlerName !== 'cli-error') {
        void model.pluginsInterface?.eventsEmitter.emit('cli-error', {
          type: 'cli-system',
          error: cliErrorToError(pluginError),
        });
      }

      pluginLogger.error(
        `Plugin "${context.pluginName}" failed while handling "${context.handlerName}".`
      );
      pluginLogger.error(stringifyError(error));
    };
  }

  const cmdName = command.name();
  if (cmdName === 'dev' || cmdName === 'build' || cmdName === 'preview') {
    await pluginsInterface?.hooksEmitter.emitSerial('cli:setup', {
      cmd: cmdName,
    });
  }

  services.errorReporter.on('errorReported', (error) => {
    // Error is already surfaced to plugins via `cli-error` event, so avoid emitting it again.
    if (
      !isType(error.cliCode, CliErrorCode.FailedToBuildAstroApp) &&
      !isType(error.cliCode, CliErrorCode.FailedToBuildExtensionsApp)
    ) {
      void pluginsInterface?.eventsEmitter.emit('cli-error', {
        type: isCliSystemError(error) ? 'cli-system' : 'cli-user',
        error: cliErrorToError(error),
      });
    }
  });

  const { panorama, biLogger } = services;

  const commandStatus = reportCommandStartEvent({
    msid: model.config.projectType === 'Site' ? model.config.siteId : undefined,
    appId: model.config.appId,
    biLogger,
    command,
    cliVersion,
    flow,
    extensions: {},
    extraFields: undefined,
  });

  const transaction = createCommandTransaction(panorama, command);

  try {
    transaction.start();
    await commandFn(model);
    transaction.finish();
    commandStatus.succeed();
  } catch (e) {
    commandStatus.failed(e);
    throw e;
  }
}
